// @ts-nocheck

import { MD5 } from 'crypto-js';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import {
  ButtonUI,
  CustomThemeProvider,
  DateRangePicker,
  Input,
  ModalUI,
  showNotification,
  Tabs,
  Tooltip,
  TooltipInteractionEnum,
  useIsMounted,
  usePortal
} from 'sonata-react-components';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';

import { copyToClipboard } from '@/helpers/reporting.helpers';
import { reportingFetchSharedReports } from '@/store/actions/reporting.actions';
import { getCampaigns } from '@/store/selectors/reportingAdvancedFilter.selectors';
import { createSharedReport, getSharedReportPermissions, updateShareReportPassword } from '../../reporting.resources';
import { ReportingSharedPermissionsSelector } from '../ReportingSharedPermissionsSelector';
import { ReportingSharedReportList } from '../ReportingSharedReportList';
import { ReportingShareReportModalContext } from '../ReportingShareReportModalContext';
import { ReportingShareReportPassword } from '../ReportingShareReportPassword';

import './reportingShareAdvanceReportModal.scss';

const DATE_FORMAT = 'DD MMM, YYYY';

function ReportingShareAdvanceReportModal({ buttonProps, user, userRoles }) {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();

  const getCurrentDateFormat = () => moment(new Date()).format('DD MMM, YYYY - HH:mm');
  const timeFormat = {
    hour: 23,
    minute: 59,
    second: 59
  };

  const initialState = {
    activeTab: 'create',
    currentSharedReportExpirationDate: moment.utc().add('7', 'days').set(timeFormat),
    currentSharedReportId: null,
    currentSharedReportLink: `${taptap.config.sonataReportingAppUrl}?report=XXXXXX`,
    currentSharedReportName: formatMessage(
      {
        defaultMessage: 'Shared report',
        id: 'reporting.shareReportModal.sharedReportNameTemplate'
      },
      { date: getCurrentDateFormat() }
    ),
    currentSharedReportPassword: '',
    defaultSharedReportPermission: [],
    generateLinkButtonDisabled: true,
    lastSharedReportId: null,
    loading: false,
    modalRef: useRef(),
    passwordHasBeenSaved: false,
    passwordLinkEnabled: false,
    passwordNeedsUpdate: false,
    passwordValid: false,
    selectedSharedReportPermission: null,
    sharedReportPermissions: null
  };

  const [state, setState] = useState(initialState);
  const isMounted = useIsMounted();
  const sharedReportToken = {};

  const {
    activeTab,
    currentSharedReportExpirationDate,
    currentSharedReportId,
    currentSharedReportLink,
    currentSharedReportName,
    currentSharedReportPassword,
    defaultSharedReportPermission,
    generateLinkButtonDisabled,
    lastSharedReportId,
    loading,
    modalRef,
    passwordHasBeenSaved,
    passwordLinkEnabled,
    passwordNeedsUpdate,
    passwordValid,
    selectedSharedReportPermission,
    sharedReportPermissions
  } = state;

  const campaignIds = useSelector(getCampaigns);

  useEffect(() => {
    (async () => {
      const existingShareReportPermissions = await getSharedReportPermissions(sharedReportToken);
      const allowedTiers = existingShareReportPermissions.filter(
        tier => !tier.requiredPermission || checkUserResourcePermissions(tier.requiredPermission, user, userRoles)
      );
      // The selector only shows with 2+ tiers. A single allowed tier is used as is; with none, Basic is the fallback
      const forcedTier =
        allowedTiers.length > 1
          ? undefined
          : (allowedTiers[0] ??
            existingShareReportPermissions.find(tier => tier.requiredPermission === 'reportingShareTierBasic'));
      setState(prevState => ({
        ...prevState,
        ...(forcedTier && {
          defaultSharedReportPermission: [forcedTier._id],
          selectedSharedReportPermission: [forcedTier._id]
        }),
        sharedReportPermissions: allowedTiers
      }));
    })();
    return () => sharedReportToken.cancel?.();
  }, []);

  useEffect(() => {
    if (selectedSharedReportPermission) {
      setState(prevState => ({
        ...prevState,
        generateLinkButtonDisabled: false
      }));
    }
  }, [selectedSharedReportPermission]);

  const passwordGenerateCallback = useCallback(async () => {
    setState(prevState => ({
      ...prevState,
      loading: true
    }));
    const passwordHashed = MD5(currentSharedReportPassword).toString();
    const isNewSharedReport = !currentSharedReportId;
    let createdSharedReport;
    try {
      if (currentSharedReportId) {
        await updateShareReportPassword(currentSharedReportId, passwordHashed);
      } else {
        createdSharedReport = await createSharedReport(
          passwordHashed,
          campaignIds,
          currentSharedReportExpirationDate,
          currentSharedReportName,
          {
            tierPermissionsId: selectedSharedReportPermission,
            type: 'default'
          }
        );
        dispatch(reportingFetchSharedReports(campaignIds, 'default'));
        showNotification(
          'success',
          formatMessage(
            {
              defaultMessage: 'Success! Your link for "{name}" is ready to be copied.',
              id: 'reporting.shareReportModal.linkGeneratedNotification'
            },
            { name: currentSharedReportName }
          ),
          {
            closable: true,
            icon: 'fal fa-check-circle'
          }
        );
      }
      setState(prevState => ({
        ...initialState,
        ...(isNewSharedReport && {
          activeTab: 'myLinks',
          lastSharedReportId: createdSharedReport?._id
        }),
        currentSharedReportName: formatMessage(
          {
            defaultMessage: 'Shared report',
            id: 'reporting.shareReportModal.sharedReportNameTemplate'
          },
          { date: getCurrentDateFormat() }
        ),
        defaultSharedReportPermission: prevState.defaultSharedReportPermission,
        loading: false,
        selectedSharedReportPermission: prevState.defaultSharedReportPermission,
        sharedReportPermissions: prevState.sharedReportPermissions
      }));
    } catch {
      if (!isMounted()) return;
      showNotification(
        'danger',
        formatMessage({
          defaultMessage: "The link couldn't be generated, please try again",
          id: 'reporting.shareReportModal.errorToast'
        }),
        {
          closable: true,
          icon: 'fal fa-exclamation-triangle'
        }
      );
      setState(prevState => ({
        ...prevState,
        loading: false,
        passwordHasBeenSaved: true
      }));
    }
  }, [
    currentSharedReportPassword,
    currentSharedReportId,
    initialState,
    campaignIds,
    currentSharedReportExpirationDate,
    currentSharedReportName,
    dispatch,
    formatMessage,
    getCurrentDateFormat,
    isMounted,
    selectedSharedReportPermission
  ]);

  const onGenerateLinkClick = useCallback(() => {
    if (loading) return;
    setState(prevState => ({
      ...prevState,
      passwordHasBeenSaved: false
    }));
    passwordGenerateCallback();
  }, [loading, passwordGenerateCallback]);

  const nameChanged = newName => {
    setState(prevState => ({
      ...prevState,
      currentSharedReportName: newName
    }));
  };

  const expirationDateChanged = newExpirationDate => {
    setState(prevState => ({
      ...prevState,
      currentSharedReportExpirationDate: moment(newExpirationDate).set(timeFormat)
    }));
  };

  const passwordCopyToClipboardCallback = useCallback(() => {
    copyToClipboard(currentSharedReportLink);
    showNotification(
      'success',
      formatMessage({
        defaultMessage: 'The link has been copied correctly',
        id: 'reporting.shareReportModal.linkToast'
      }),
      {
        closable: true,
        icon: 'fal fa-check-circle'
      }
    );
  }, [currentSharedReportLink, formatMessage]);

  const closeModalCallback = useCallback(() => {
    setState({
      ...initialState,
      sharedReportPermissions
    });
  }, [sharedReportPermissions, initialState]);

  const targetBody = usePortal('sharedReportModalContainer');

  const isGenerateLinkDisabled = !passwordValid || generateLinkButtonDisabled;

  const sharedReportModal = useCallback(() => {
    const generateLinkButton = (
      <ButtonUI
        disabled={isGenerateLinkDisabled}
        icon="fal fa-arrow-right"
        iconPosition="right"
        id="advance_shared_report_modal_generate_button"
        loading={loading}
        onClick={onGenerateLinkClick}
        shape="squareRounded"
        size="medium"
        text={formatMessage({
          defaultMessage: `${passwordNeedsUpdate ? 'Update Link' : 'Generate Link'}`,
          id: `reporting.shareReportPassword.button${passwordNeedsUpdate ? 'Update' : ''}Text`
        })}
      />
    );
    return ReactDOM.createPortal(
      <ReportingShareReportModalContext.Provider
        value={{
          currentSharedReportExpirationDate,
          currentSharedReportId,
          currentSharedReportLink,
          currentSharedReportName,
          currentSharedReportPassword,
          defaultSharedReportPermission,
          generateLinkButtonDisabled,
          loading,
          passwordHasBeenSaved,
          passwordLinkEnabled,
          passwordNeedsUpdate,
          passwordValid,
          selectedSharedReportPermission,
          setContext: setState,
          sharedReportPermissions
        }}
      >
        <CustomThemeProvider themeContrast="light">
          <ModalUI
            className="advanceSharedReportModal"
            closeModalCallback={closeModalCallback}
            description={formatMessage({
              defaultMessage: 'Manage your active links or create a new one.',
              id: 'reporting.shareReportModal.description'
            })}
            maxHeight="985px"
            ref={modalRef}
            title={formatMessage({
              defaultMessage: 'Share Report',
              id: 'reporting.shareReportModal.title'
            })}
            width="800px"
          >
            <div className="advanceSharedReportModal__container">
              <Tabs
                className="advanceSharedReportModal__tabs"
                onChangeTab={(tabId: string) =>
                  setState(prevState => ({
                    ...prevState,
                    activeTab: tabId,
                    ...(tabId === 'create' && { lastSharedReportId: null })
                  }))
                }
                tabSelected={activeTab}
                tabs={[
                  {
                    component: (
                      <>
                        {(sharedReportPermissions?.length ?? 2) > 1 && (
                          <div className="advanceSharedReportModal__permissionSelectorContainer">
                            <div className="advanceSharedReportModal__sectionHeader">
                              {formatMessage({
                                defaultMessage:
                                  'Select the type of permissions and access you want to give to the report',
                                id: 'reporting.shareReportModal.permissionSelectorHeader'
                              })}
                            </div>
                            <ReportingSharedPermissionsSelector />
                          </div>
                        )}
                        <div className="advanceSharedReportModal__generateLinkContainer">
                          <div className="advanceSharedReportModal__sectionHeader">
                            {formatMessage({
                              defaultMessage: `Add a password and click "Generate link". You'll be able to copy it directly from your list.`,
                              id: 'reporting.shareReportModal.generateLinkHeader'
                            })}
                          </div>
                          <form className="advanceSharedReportModal__generateLinkDetails">
                            <div className="advanceSharedReportModal__generateLinkName">
                              <Input
                                defaultValue={currentSharedReportName}
                                id="advance_shared_report_modal_name_input"
                                label={formatMessage({
                                  defaultMessage: 'Link name',
                                  id: 'reporting.shareReportModal.tagLink'
                                })}
                                onChange={nameChanged}
                                shape="squareUI"
                                type="text"
                                value={currentSharedReportName}
                              />
                            </div>
                            <div className="advanceSharedReportModal__generateLinkExpirationDate">
                              <DateRangePicker
                                date={currentSharedReportExpirationDate}
                                displayFormat={DATE_FORMAT}
                                id="advance_shared_report_modal_expiration_date_input"
                                label={formatMessage({
                                  defaultMessage: 'Expiration date',
                                  id: 'reporting.shareReportModal.expirationDate'
                                })}
                                noBorder
                                numberOfMonths={1}
                                onDateChange={expirationDateChanged}
                                placeholder=""
                                readOnly
                                singlePicker
                              />
                            </div>
                          </form>
                          <ReportingShareReportPassword />
                        </div>
                        <div className="advanceSharedReportModal__linkButtonGenerator">
                          {isGenerateLinkDisabled ? (
                            <Tooltip
                              config={{
                                arrow: true,
                                interactions: [TooltipInteractionEnum.hover],
                                placement: 'top',
                                strategy: 'fixed',
                                text: generateLinkButtonDisabled
                                  ? formatMessage({
                                      defaultMessage: 'Select a permissions type to generate the link',
                                      id: 'reporting.shareReportModal.generateLinkPermissionsTooltip'
                                    })
                                  : formatMessage({
                                      defaultMessage: 'Your password must meet all requirements',
                                      id: 'reporting.shareReportModal.generateLinkPasswordTooltip'
                                    })
                              }}
                            >
                              <span className="advanceSharedReportModal__disabledActionWrapper">
                                {generateLinkButton}
                              </span>
                            </Tooltip>
                          ) : (
                            generateLinkButton
                          )}
                        </div>
                      </>
                    ),
                    icon: {
                      content: 'fa-solid fa-plus',
                      position: 'left'
                    },
                    id: 'create',
                    title: formatMessage({
                      defaultMessage: 'Create link',
                      id: 'reporting.shareReportModal.tabs.create'
                    })
                  },
                  {
                    component: <ReportingSharedReportList sharedReportId={lastSharedReportId} />,
                    icon: {
                      content: 'fa-solid fa-share-nodes',
                      position: 'left'
                    },
                    id: 'myLinks',
                    title: formatMessage({
                      defaultMessage: 'My Links',
                      id: 'reporting.shareReportModal.tabs.myLinks'
                    })
                  }
                ]}
              />
            </div>
          </ModalUI>
        </CustomThemeProvider>
      </ReportingShareReportModalContext.Provider>,
      targetBody
    );
  }, [
    activeTab,
    currentSharedReportName,
    currentSharedReportExpirationDate,
    currentSharedReportId,
    currentSharedReportPassword,
    passwordGenerateCallback,
    passwordHasBeenSaved,
    passwordLinkEnabled,
    passwordNeedsUpdate,
    passwordValid,
    sharedReportPermissions,
    closeModalCallback,
    currentSharedReportLink,
    defaultSharedReportPermission,
    expirationDateChanged,
    formatMessage,
    generateLinkButtonDisabled,
    isGenerateLinkDisabled,
    lastSharedReportId,
    loading,
    modalRef,
    nameChanged,
    onGenerateLinkClick,
    selectedSharedReportPermission,
    targetBody
  ]);

  const showSharedReportModal = useCallback(() => {
    setState(prevState => ({
      ...prevState,
      activeTab: 'create',
      currentSharedReportName: formatMessage(
        {
          defaultMessage: 'Shared report',
          id: 'reporting.shareReportModal.sharedReportNameTemplate'
        },
        { date: getCurrentDateFormat() }
      )
    }));
    modalRef && modalRef.current && modalRef.current.openModal();
  }, [modalRef, formatMessage, getCurrentDateFormat]);

  return (
    <>
      <ButtonUI {...buttonProps} onClick={showSharedReportModal} />
      {sharedReportModal()}
    </>
  );
}

ReportingShareAdvanceReportModal.propTypes = {
  buttonProps: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  userRoles: PropTypes.array.isRequired
};

export default React.memo(ReportingShareAdvanceReportModal);
